/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023 
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */
import { currentConfig } from "amplifyConfig";
import { Auth } from "aws-amplify";
import dayjs from 'dayjs';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { useNavStore, useUserStore } from "state";
import { useToastAlertStore } from "state/ToastAlertStore";
import { useLoginStore } from "state/LoginStore";
import { useTableStore } from 'state/TableStore';
import { Props } from "../interfaces/hooks/useAuthInterfaces";
import { useMUITheme } from '../theme/ThemeProvider';
import { urls } from '../urls';
import { getDomainName } from "../utils/getDomainName";
import { sendPreAuthAudit, getExpTime } from './shared/helpers/';
import { useAudit } from "./useAudit";
import { useRapbackSubscription } from "./useRapbackSubscription";
import { useUserCognito } from "./useUserCognito";
import AuthContext from "./shared/contexts/AuthContext";
import { fetchLogoutTokenHandler } from "./services/useAuth/";
import { useHandleAcceptTerms, useHandleMFASubmit, executeLogin, useResetForceResetPassword, useResetForgotPassword, useResetMFA, useSendForgotPassCode, useSetToken } from "./fragments/useAuth"

console.log(currentConfig); // DO NOT REMOVE THIS LINE

export const AuthProvider = ({ children, ...props }: Props) => {
  const [, , removeCookie] = useCookies();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>();
  const [authCode, setAuthCode] = useState({});
  const [mfa, setMFA] = useState(false);
  const [forceResetPassword, setForceResetPassword] = useState(false);
  const [userForgotPassword, setForgotPassword] = useState(false);
  const [pwChangedTitle,] = useState("");
  const [pwChangedDescrip,] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [authFlowError, setAuthFlowError] = useState("");
  const [authFlowErrorType, setAuthFlowErrorType] = useState('');
  const [loading, setLoading] = useState(false);
  const { addAuditEvent } = useAudit();
  const authUrl = urls.AUTH;
  const { mode } = useMUITheme();
  const { saveTheme, } = useMUITheme()
  const userConfig = useUserStore(state => state.userConfig)
  const setUserConfig = useUserStore(state => state.setUserConfig)
  const config = useUserStore(state => state.config)
  const { loggedIn } = userConfig
  const resetUserStore = useUserStore(state => state.resetUserStore)
  const resetTableStore = useTableStore(state => state.resetTableStore)
  const resetNavStore = useNavStore(state => state.resetNavStore)
  const setTermsAccepted = useLoginStore(state => state.setTermsAccepted)
  const clearAlerts = useToastAlertStore(state => state.clearAlerts)

  console.log(`debug group call running useUserCognito from useAuth`)

  const setUserConfigLoaded = useUserStore(state => state.setUserConfigLoaded)
  const { userRole, userGroup, usersPermissions, groups, userGivenName, userFamilyName, userEmail } = useUserCognito();
  let tokenHandler = (process.env.REACT_APP_USE_MULTI_TOKEN_HANDLER === 'true') ? 'multi-token-handler' : 'token-handler';
  const setOpenAlert = useLoginStore((state) => state.setOpenAlert)
  const setSeverity = useLoginStore((state) => state.setSeverity)
  const userActive = useTableStore(state => state.userActive)
  const userAcceptingTerms = useLoginStore(state => state.userAcceptingTerms)
  const setUserAcceptingTerms = useLoginStore(state => state.setUserAcceptingTerms)
  const termsAccepted = useLoginStore(state => state.termsAccepted)
  const searchParams = useTableStore((state) => state.searchParams)

  const { fetchMaintenance, refetchParams } = useRapbackSubscription(
    "transaction-summary",
    25,
    1,
    userConfig.userRole,
    searchParams,
    true,
    "rapback-prenotifications",
    true
  )

  const login = async (loginData: any) => {
    return executeLogin(loginData, setUser, setAuthFlowErrorType, setAuthFlowError)
  }

  const cancelFlow = () => {
    setMFA(false)
    setAuthFlowError("")
    setAuthFlowErrorType("")
    setForceResetPassword(false)
    setForgotPassword(false)
    setUserAcceptingTerms(false)
  }

  useEffect(() => {
    console.log('debug token refresh user updated: ', user)
    if (user) {
      const { challengeName } = user;
      const { preferredMFA } = user;
      setAuthCode("");
      const setupMFA = async () => {
        try {
          try {
            await sendPreAuthAudit(user.getUsername(), "Setup MFA Attempt", `${user.getUsername()} attempted to setup MFA in CARES`)
          } catch (err) {
            console.error(`Failed Setup MFA audit ${err}`)
          }
          const res = await Auth.setupTOTP(user);
          const code =
            "otpauth://totp/AWSCognito:" +
            user.username +
            "?secret=" +
            res +
            "&issuer=Cognito";
          setAuthCode({ auth: code, text: res });
          setMFA(true);
        } catch (e: any) {
          setSeverity("error")
          setOpenAlert(true)
          setAuthFlowError(e.message);
        }
      };

      console.log('user signin: ', {
        user,
        challengeName,
        preferredMFA,
      })

      if (challengeName === "MFA_SETUP" || (preferredMFA === "NOMFA" && process.env.REACT_APP_LOWER_ENV === "false")) {
        setupMFA();
      } else if (challengeName === "SOFTWARE_TOKEN_MFA") {
        setMFA(true);
      } else if (challengeName === "NEW_PASSWORD_REQUIRED") {
        setForceResetPassword(true);
      } else if (preferredMFA === "NOMFA" && process.env.REACT_APP_LOWER_ENV === "true") {
        setMFA(false)
        setToken(user)
        console.log("prefferedMFA", preferredMFA);
      }
      console.log("challengeName", challengeName);
    }
  }, [user]);

  const { sendForgotPassCode } = useSendForgotPassCode(setForgotPassword, setLoading, setAuthFlowErrorType, setAuthFlowError)

  const forgotPassword = async () => {
    setForgotPassword(true);
  }

  const handleResetPassword = useCallback(async (newPass: string, code = null, userName: string, oldPass: string) => {
    setLoading(true);

    try {
      if (!code) {
        let res = await Auth.completeNewPassword(user, newPass, {
          given_name: user.challengeParam.userAttributes.given_name,
          family_name: user.challengeParam.userAttributes.family_name,
        });
        if (res) {
          setUser(res);
          setOpenAlert(true)
          setSeverity("success")
          setForceResetPassword(false);
          await addAuditEvent(
            "Password Changed",
            `${userName} reset password on  ${new Date()}`,
            new Date(),
            undefined
          );
          setLoading(false);
          return res;
        }
      } else if (userForgotPassword && code) {
        try {
          let res = await Auth.forgotPasswordSubmit(userName, code, newPass);

          if (res) {
            try {
              await sendPreAuthAudit(userName, "Password Changed", `${userName} reset password on  ${new Date()}`);
            } catch (err) {
              console.error(`Failed Password Change audit ${err}`)
            }
            setForgotPassword(false);
            setOpenAlert(true)
            setSeverity("success")
            return res;
          }
        } catch (e: any) {
          setAuthFlowError(e.message);
          setSeverity("error")
          setOpenAlert(true)
        }
      }
    } catch (e: any) {
      setAuthFlowError(e.message);
    } finally {
      setLoading(false);
    }

  }, [addAuditEvent, user, userForgotPassword]);

  useEffect(() => {
    console.log('debug userConfig loggedIn updated: ', userConfig.loggedIn)
  }, [userConfig.loggedIn])

  const { setToken } = useSetToken(setLoading);

  const refreshAuthToken = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const currentSession = await Auth.currentSession();
      const refreshToken = currentSession.getRefreshToken();
      await new Promise((resolve, reject) => {
        user.refreshSession(refreshToken, (err, session) => {
          if (err) {
            reject(err);
          } else {
            resolve(session);
          }
        });
      });
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
  };

  useEffect(() => {
    if (loggedIn) {
      const intervalId = setInterval(async () => {
        const user = await Auth.currentAuthenticatedUser();
        const { jwtToken } = user.signInUserSession.accessToken;
        const expTime = getExpTime(jwtToken);

        let now = dayjs(Date.now())
        let expirationTime = dayjs(expTime)

        let diffInMins = expirationTime.diff(now, "minutes")

        if (diffInMins <= 5 && userActive) {
          await refreshAuthToken();
        }

      }, 5000);

      return () => clearInterval(intervalId);
    }

  }, [userActive, loggedIn]);

  useEffect(() => {
    let userRoles
    if (config.cognito) {
      userRoles = config.cognito.userRoles
    }

    const updateUserConfig = userGroup && groups && userGivenName && userFamilyName && userRole && userEmail && userRoles?.length > 0 && Object.keys(usersPermissions)?.length > 0

    console.log('DEBUG THEME: useAuth useEffect triggered', {
      updateUserConfig,
      hasUser: !!user,
      username: user?.username,
      pendingTheme: localStorage.getItem("pendingThemeSave"),
      configTheme: config?.cognito ? 'has config' : 'no config',
      userGroup,
      userRole
    });

    if (updateUserConfig) {
      console.log('userRoles: ', { userRoles, userRole })
      const isAdmin = userRole === "mc_no_mfa" ? true : userRoles.find(role => role.label === userRole).isAdmin.toString()

      const currentUserConfig = useUserStore.getState().userConfig;

      const newUserConfig = {
        ...currentUserConfig,
        userRole,
        isAdmin,
        userGroup,
        usersPermissions,
        groups,
        userGivenName,
        userFamilyName,
        userEmail
      }

      setUserConfig(newUserConfig)

      // Check for pending theme update from pre-login/MFA changes (now in localStorage)
      const pendingTheme = localStorage.getItem("pendingThemeSave");

      console.log('DEBUG THEME: About to apply theme', {
        pendingTheme,
        storedTheme: newUserConfig.theme,
        hasUsername: !!user?.username,
        currentLocalStorage: localStorage.getItem('theme')
      });

      if (pendingTheme && user?.username) {
        // User changed theme during login/MFA flow - apply it
        console.log(`DEBUG THEME: Applying pending theme update: ${pendingTheme}`);
        saveTheme(pendingTheme, user.username);
        localStorage.removeItem("pendingThemeSave");
      } else if (newUserConfig.theme && user?.username && !pendingTheme) {
        // No pending changes - apply stored user theme
        console.log(`DEBUG THEME: Applying stored user theme: ${newUserConfig.theme}`);
        saveTheme(newUserConfig.theme, user.username);
      } else {
        console.log('DEBUG THEME: No theme applied - conditions not met');
      }
    }

  }, [config, userRole, userGroup, usersPermissions, groups, userGivenName, userFamilyName, userEmail, saveTheme, user])

  useEffect(() => {
    const userConfigMissingElement = Object.keys(userConfig).some((key) => {
      return !userConfig[key]
    })

    if (!userConfigMissingElement) {
      setUserConfigLoaded(true)
    }
  }, [userConfig])

  const clearStorageRefresh = useCallback(() => {
    console.log('debug groupsService clearStorageRefresh running');

    if (process.env.NODE_ENV != 'production') {
      removeCookie("token", { path: "/", domain: getDomainName() });
    }

    useUserStore.persist.clearStorage();
    useTableStore.persist.clearStorage();
    useNavStore.persist.clearStorage();
    useToastAlertStore.getState().clearAlerts();

    console.log('debug reset userStore');
    resetUserStore();
    resetTableStore();
    resetNavStore();
    clearAlerts();
    if (saveTheme) {
      saveTheme('light')
    } else {
      localStorage.setItem('theme', 'light')
    }
  }, [removeCookie, resetUserStore, resetTableStore, resetNavStore]);

  const logoutTokenHandler = useCallback(async () => {
    await fetchLogoutTokenHandler(tokenHandler, clearStorageRefresh)
  }, [authUrl, clearStorageRefresh, tokenHandler])

  const logout = useCallback(async () => {
    console.log("debug groupsService logout idle: in useAuth logout");
    setMFA(false);
    navigate("/", { replace: true });
    try {
      const { username } = await Auth.currentAuthenticatedUser();
      await addAuditEvent(
        "Logout",
        `${username} logged out of CARES dashboard at ${new Date()}`,
        new Date(),
        undefined
      );

    } catch (e: any) {
      console.log('Error logging out: ', e)
      clearStorageRefresh()
    }

    if (process.env.NODE_ENV != 'production') {
      clearStorageRefresh()
    } else {
      logoutTokenHandler()
    }
    clearAlerts();
    setLoginLoading(false)
  }, [navigate, addAuditEvent, clearStorageRefresh, logoutTokenHandler]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === null && event.newValue === null && !localStorage.getItem("access_token")) {
        localStorage.setItem("theme", mode);
        window.location.reload();
        console.log('Logout triggered from another tab');
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (termsAccepted && Object.keys(refetchParams).length) {
      console.log('debug rb sub useEffect dependencies running fetch from useAuth deps: ', {
        termsAccepted,
        refetchParams
      })
      fetchMaintenance(refetchParams.queryDataRequest)
      setTermsAccepted(false)
    }
  }, [termsAccepted, refetchParams])

  const { handleAcceptTerms } = useHandleAcceptTerms(setMFA);

  const { handleMFASubmit } = useHandleMFASubmit(user, setToken, setLoginLoading, setAuthFlowErrorType, setAuthFlowError);

  const { resetMFA } = useResetMFA(setMFA)
  const { resetForceResetPassword } = useResetForceResetPassword(setForceResetPassword)
  const { resetForgotPassword } = useResetForgotPassword(setForgotPassword);

  const value = useMemo(
    () => ({
      login,
      logout,
      handleMFASubmit,
      handleResetPassword,
      handleAcceptTerms,
      forgotPassword,
      sendForgotPassCode,
      resetMFA,
      resetForceResetPassword,
      resetForgotPassword,
      setToken,
      cancelFlow,
      setLoginLoading,
      clearStorageRefresh,
      user,
      authCode,
      mfa,
      userForgotPassword,
      userAcceptingTerms,
      forceResetPassword,
      authFlowError,
      pwChangedDescrip,
      pwChangedTitle,
      loginLoading,
      authFlowErrorType,
      loading,
      setAuthFlowError,
      refreshAuthToken,
    }),
    [
      user,
      mfa,
      authCode,
      userForgotPassword,
      userAcceptingTerms,
      forceResetPassword,
      authFlowError,
      pwChangedDescrip,
      pwChangedTitle,
      loginLoading,
      authFlowErrorType,
      loading,
      cancelFlow,
      resetForgotPassword,
      resetMFA,
      resetForceResetPassword,
      logout,
      sendForgotPassCode,
      handleMFASubmit,
      handleResetPassword,
      handleAcceptTerms,
      setToken,
      setLoginLoading,
      setAuthFlowError,
      clearStorageRefresh
    ]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return useContext(AuthContext);
};
